import NepaliDate from 'nepali-date-converter';
import {
  startOfToday,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import dayjs from 'dayjs';
import { decryptData } from '@/components/reusable/crypto/Cipher';

export const getTodayDate = () => {
  const todayBsDate = new NepaliDate();

  const bsTodayDate = `${todayBsDate.getYear()}-${(todayBsDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${todayBsDate.getDate().toString().padStart(2, '0')}`;

  return bsTodayDate;
};

export const getBsFromAD = (value: string) => {
  try {
    if (!value) return null;
    const adDate = new Date(value);
    if (isNaN(adDate.getTime())) return null;
    const bsDate = NepaliDate.fromAD(adDate);
    return bsDate?.format?.('YYYY-MM-DD') || null;
  } catch {
    return null;
  }
};

export const disableDateBetween = (
  startDate?: string | dayjs.Dayjs | null,
  endDate?: string | dayjs.Dayjs | null,
) => {
  return (current: dayjs.Dayjs) => {
    if (!current || !startDate || !endDate) return false;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return current.isBefore(start, 'day') || current.isAfter(end, 'day');
  };
};

const raw = localStorage.getItem('userData');
export const userData: any = raw ? decryptData(raw) : null;

export const warningTitle =
  'Are you sure you want to do transaction in an inactive fiscal year';

export const dateType = userData?.dateType?.toLowerCase();

export const periodArrayNepali = [
  {
    label: 'Today',
    value: {
      dateTime: new Date().toISOString(),
      from: {
        date: getBsFromAD(new Date().toISOString().split('T')[0]),
      },
      to: {
        dateTime: new Date().toISOString(),
        date: getBsFromAD(new Date().toISOString().split('T')[0]),
      },
    },
  },
  {
    label: 'Yesterday',
    value: (() => {
      const y = subDays(startOfToday(), 1);
      return {
        from: {
          dateTime: new Date(y.setHours(23, 59, 59)).toISOString(),
          date: getBsFromAD(y.toISOString().split('T')[0]),
        },
        to: {
          dateTime: new Date(y.setHours(23, 59, 59)).toISOString(),
          date: getBsFromAD(y.toISOString().split('T')[0]),
        },
      };
    })(),
  },
  {
    label: 'This Week',
    value: {
      from: (() => {
        const from = startOfWeek(new Date(), { weekStartsOn: 1 });
        return {
          dateTime: from.toISOString(),
          date: getBsFromAD(from.toISOString().split('T')[0]),
        };
      })(),
      to: {
        dateTime: new Date().toISOString(),
        date: getBsFromAD(new Date().toISOString().split('T')[0]),
      },
    },
  },
  {
    label: 'Last 7 Days',
    value: {
      from: (() => {
        const from = subDays(new Date(), 6);
        return {
          dateTime: from.toISOString(),
          date: getBsFromAD(from.toISOString().split('T')[0]),
        };
      })(),
      to: {
        dateTime: new Date().toISOString(),
        date: getBsFromAD(new Date().toISOString().split('T')[0]),
      },
    },
  },
  {
    label: 'Last Week',
    value: {
      from: (() => {
        const from = startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
        return {
          dateTime: from.toISOString(),
          date: getBsFromAD(from.toISOString().split('T')[0]),
        };
      })(),
      to: (() => {
        const to = endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
        return {
          dateTime: to.toISOString(),
          date: getBsFromAD(to.toISOString().split('T')[0]),
        };
      })(),
    },
  },
  {
    label: 'This Month',
    value: (() => {
      const todayBsDate = new NepaliDate();
      const year = todayBsDate.getYear();
      const month = todayBsDate.getMonth();
      const daysInMonth = new NepaliDate(year, month, 0).getDate();
      return {
        from: {
          dateTime: new Date().toISOString(),
          date: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        },
        to: {
          dateTime: new Date().toISOString(),
          date: `${year}-${String(month + 1).padStart(2, '0')}-${daysInMonth}`,
        },
      };
    })(),
  },

  {
    label: 'Last Month',
    value: (() => {
      const todayBsDate = new NepaliDate();
      const currentYear = todayBsDate.getYear();
      const currentMonth = todayBsDate.getMonth();

      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const year = currentMonth === 0 ? currentYear - 1 : currentYear;

      const daysInLastMonth = new NepaliDate(year, lastMonth + 1, 0).getDate();

      return {
        from: {
          dateTime: new Date().toISOString(),
          date: `${year}-${String(lastMonth + 1).padStart(2, '0')}-01`,
        },
        to: {
          dateTime: new Date().toISOString(),
          date: `${year}-${String(lastMonth + 1).padStart(2, '0')}-${daysInLastMonth}`,
        },
      };
    })(),
  },

  {
    label: 'Last 3 Months',
    value: (() => {
      const today = new NepaliDate();
      let year = today.getYear();
      let month = today.getMonth();

      month -= 2;
      while (month < 0) {
        month += 12;
        year -= 1;
      }

      const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const to = `${today.getYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${new NepaliDate(today.getYear(), today.getMonth() + 1, 0).getDate()}`;

      return {
        from: {
          dateTime: new Date().toISOString(),
          date: from,
        },
        to: {
          dateTime: new Date().toISOString(),
          date: to,
        },
      };
    })(),
  },

  {
    label: 'All',
    value: {
      from: (() => {
        const today = new Date();
        const nepaliToday = new NepaliDate(today);
        nepaliToday.setYear(nepaliToday.getYear() - 1);
        const date =
          getBsFromAD(userData?.financialStartYear) ||
          nepaliToday.format('YYYY-MM-DD');
        return {
          dateTime: date,
          date,
        };
      })(),
      to: (() => {
        const nepaliNow = new NepaliDate(new Date());
        const date =
          getBsFromAD(userData?.financialEndYear) ||
          nepaliNow.format('YYYY-MM-DD');
        return {
          dateTime: date,
          date,
        };
      })(),
    },
  },
];

export const periodArrayEnglish = [
  {
    label: 'Today',
    value: {
      from: (() => {
        const now = new Date();
        const date = now.toLocaleDateString('en-CA');
        return {
          dateTime: `${date}T00:00:00.000Z`,
          date,
        };
      })(),
      to: (() => {
        const now = new Date();
        const date = now.toLocaleDateString('en-CA');
        return {
          dateTime: now.toISOString(),
          date,
        };
      })(),
    },
  },
  {
    label: 'Yesterday',
    value: (() => {
      const y = subDays(startOfToday(), 1);
      return {
        from: {
          dateTime: new Date(y.setHours(23, 59, 59)).toISOString(),
          date: y.toISOString().split('T')[0],
        },
        to: {
          dateTime: new Date(y.setHours(23, 59, 59)).toISOString(),
          date: y.toISOString().split('T')[0],
        },
      };
    })(),
  },
  {
    label: 'This Week',
    value: {
      from: (() => {
        const from = startOfWeek(new Date(), { weekStartsOn: 1 });
        return {
          dateTime: from.toISOString(),
          date: from.toISOString().split('T')[0],
        };
      })(),
      to: {
        dateTime: new Date().toISOString(),
        date: new Date().toISOString().split('T')[0],
      },
    },
  },
  {
    label: 'Last 7 Days',
    value: {
      from: (() => {
        const from = subDays(new Date(), 6);
        return {
          dateTime: from.toISOString(),
          date: from.toISOString().split('T')[0],
        };
      })(),
      to: {
        dateTime: new Date().toISOString(),
        date: new Date().toISOString().split('T')[0],
      },
    },
  },
  {
    label: 'Last Week',
    value: {
      from: (() => {
        const from = startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
        return {
          dateTime: from.toISOString(),
          date: from.toISOString().split('T')[0],
        };
      })(),
      to: (() => {
        const to = endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
        return {
          dateTime: to.toISOString(),
          date: to.toISOString().split('T')[0],
        };
      })(),
    },
  },
  {
    label: 'This Month',
    value: {
      from: (() => {
        const from = startOfMonth(new Date());
        const date = from.toLocaleDateString('en-CA'); // YYYY-MM-DD
        return {
          dateTime: `${date}T00:00:00.000Z`, // if needed in ISO format
          date,
        };
      })(),
      to: (() => {
        const now = new Date();
        const date = now.toLocaleDateString('en-CA');
        return {
          dateTime: now.toISOString(),
          date,
        };
      })(),
    },
  },

  {
    label: 'Last Month',
    value: {
      from: (() => {
        const now = new Date();
        const from = startOfMonth(subMonths(now, 1));
        const date = from.toLocaleDateString('en-CA');
        return {
          dateTime: `${date}T00:00:00.000Z`,
          date,
        };
      })(),
      to: (() => {
        const now = new Date();
        const to = endOfMonth(subMonths(now, 1));
        const date = to.toLocaleDateString('en-CA');
        return {
          dateTime: `${date}T23:59:59.999Z`,
          date,
        };
      })(),
    },
  },

  {
    label: 'Last 3 Months',
    value: {
      from: (() => {
        const from = startOfMonth(subMonths(new Date(), 3));
        const date = from.toLocaleDateString('en-CA');
        return {
          dateTime: `${date}T00:00:00.000Z`,
          date,
        };
      })(),
      to: (() => {
        const now = new Date();
        const date = now.toLocaleDateString('en-CA');
        return {
          dateTime: now.toISOString(),
          date,
        };
      })(),
    },
  },

  // {
  //   label: 'All',
  //   value: {
  //     from: (() => {
  //       const now = new Date();
  //       const from = new Date(now);
  //       from.setFullYear(now.getFullYear() - 1);
  //       const date =
  //         userData?.financialStartYear || from.toLocaleDateString('en-CA');
  //       return {
  //         dateTime: `${date}T00:00:00.000Z`,
  //         date,
  //       };
  //     })(),
  //     to: (() => {
  //       const now = new Date();
  //       const date =
  //         userData?.financialEndYear || now.toLocaleDateString('en-CA');
  //       return {
  //         dateTime: now.toISOString(),
  //         date,
  //       };
  //     })(),
  //   },
  // },
];

// export const periodArrayNepali = [
//   {
//     label: 'Today',
//     value: {
//       from: {
//         dateTime: new Date().toISOString(),
//         date: getBsFromAD(new Date().toISOString().split('T')[0]),
//       },
//       to: {
//         dateTime: new Date().toISOString(),
//         date: getBsFromAD(new Date().toISOString().split('T')[0]),
//       },
//     },
//   },
//   {
//     label: 'Yesterday',
//     value: (() => {
//       const y = subDays(startOfToday(), 1);
//       return {
//         from: {
//           dateTime: new Date(y.setHours(23, 59, 59)).toISOString(),
//           date: getBsFromAD(y.toISOString().split('T')[0]),
//         },
//         to: {
//           dateTime: new Date(y.setHours(23, 59, 59)).toISOString(),
//           date: getBsFromAD(y.toISOString().split('T')[0]),
//         },
//       };
//     })(),
//   },
//   {
//     label: 'This Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(new Date(), { weekStartsOn: 1 });
//         return {
//           dateTime: from.toISOString(),
//           date: getBsFromAD(from.toISOString().split('T')[0]),
//         };
//       })(),
//       to: {
//         dateTime: new Date().toISOString(),
//         date: getBsFromAD(new Date().toISOString().split('T')[0]),
//       },
//     },
//   },
//   {
//     label: 'Last 7 Days',
//     value: {
//       from: (() => {
//         const from = subDays(new Date(), 6);
//         return {
//           dateTime: from.toISOString(),
//           date: getBsFromAD(from.toISOString().split('T')[0]),
//         };
//       })(),
//       to: {
//         dateTime: new Date().toISOString(),
//         date: getBsFromAD(new Date().toISOString().split('T')[0]),
//       },
//     },
//   },
//   {
//     label: 'Last Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         return {
//           dateTime: from.toISOString(),
//           date: getBsFromAD(from.toISOString().split('T')[0]),
//         };
//       })(),
//       to: (() => {
//         const to = endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         return {
//           dateTime: to.toISOString(),
//           date: getBsFromAD(to.toISOString().split('T')[0]),
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Month',
//     value: (() => {
//       const todayBsDate = new NepaliDate();
//       const year = todayBsDate.getYear();
//       const month = todayBsDate.getMonth();
//       const daysInMonth = new NepaliDate(year, month, 0).getDate();
//       return {
//         from: {
//           dateTime: new Date().toISOString(),
//           date: `${year}-${String(month + 1).padStart(2, '0')}-01`,
//         },
//         to: {
//           dateTime: new Date().toISOString(),
//           date: `${year}-${String(month + 1).padStart(2, '0')}-${daysInMonth}`,
//         },
//       };
//     })(),
//   },

//   {
//     label: 'Last Month',
//     value: (() => {
//       const todayBsDate = new NepaliDate();
//       const currentYear = todayBsDate.getYear();
//       const currentMonth = todayBsDate.getMonth();

//       const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
//       const year = currentMonth === 0 ? currentYear - 1 : currentYear;

//       const daysInLastMonth = new NepaliDate(year, lastMonth + 1, 0).getDate();

//       return {
//         from: {
//           dateTime: new Date().toISOString(),
//           date: `${year}-${String(lastMonth + 1).padStart(2, '0')}-01`,
//         },
//         to: {
//           dateTime: new Date().toISOString(),
//           date: `${year}-${String(lastMonth + 1).padStart(2, '0')}-${daysInLastMonth}`,
//         },
//       };
//     })(),
//   },

//   {
//     label: 'Last 3 Months',
//     value: (() => {
//       const today = new NepaliDate();
//       let year = today.getYear();
//       let month = today.getMonth();

//       month -= 2;
//       while (month < 0) {
//         month += 12;
//         year -= 1;
//       }

//       const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
//       const to = `${today.getYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${new NepaliDate(today.getYear(), today.getMonth() + 1, 0).getDate()}`;

//       return {
//         from: {
//           dateTime: new Date().toISOString(),
//           date: from,
//         },
//         to: {
//           dateTime: new Date().toISOString(),
//           date: to,
//         },
//       };
//     })(),
//   },

//   {
//     label: 'All',
//     value: {
//       from: (() => {
//         const today = new Date();
//         const nepaliToday = new NepaliDate(today);
//         nepaliToday.setYear(nepaliToday.getYear() - 1);
//         const date =
//           getBsFromAD(userData?.financialStartYear) ||
//           nepaliToday.format('YYYY-MM-DD');
//         return {
//           dateTime: date,
//           date,
//         };
//       })(),
//       to: (() => {
//         const nepaliNow = new NepaliDate(new Date());
//         const date =
//           getBsFromAD(userData?.financialEndYear) ||
//           nepaliNow.format('YYYY-MM-DD');
//         return {
//           dateTime: date,
//           date,
//         };
//       })(),
//     },
//   },
// ];

export const compareFinancialEndDate = (date: string) => {
  const date1 = new Date(date);
  const date2 = new Date(userData?.financialEndYear);
  return date1 > date2 ? userData?.financialEndYear : date;
};

export const compareFinancialStartDate = (date: string) => {
  const date1 = new Date(date);
  const date2 = new Date(userData?.financialStartYear);
  return date1 < date2 ? userData?.financialStartYear : date;
};

// export const periodArrayNepali = [
//   {
//     label: 'Today',
//     value: {
//       from: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Yesterday',
//     value: {
//       from: (() => {
//         const y = subDays(new Date(), 1);
//         const date = y.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const y = subDays(new Date(), 1);
//         const date = y.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(new Date(), { weekStartsOn: 1 });
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialEndDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last 7 Days',
//     value: {
//       from: (() => {
//         const from = subDays(new Date(), 6);
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialEndDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const to = endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         const date = to.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Month',
//     value: {
//       from: (() => {
//         const from = startOfMonth(new Date());
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialEndDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last Month',
//     value: {
//       from: (() => {
//         const from = startOfMonth(subMonths(new Date(), 1));
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const to = endOfMonth(subMonths(new Date(), 1));
//         const date = to.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last 3 Months',
//     value: {
//       from: (() => {
//         const from = startOfMonth(subMonths(new Date(), 3));
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = getBsFromAD(compareFinancialStartDate(date));
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = getBsFromAD(compareFinancialEndDate(date));
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'All',
//     value: {
//       from: (() => {
//         const date = getBsFromAD(userData?.financialStartYear);
//         return {
//           dateTime: `${date}T00:00:00.000Z`,
//           date,
//         };
//       })(),
//       to: (() => {
//         const date = getBsFromAD(userData?.financialEndYear);
//         return {
//           dateTime: `${date}T23:59:59.999Z`,
//           date,
//         };
//       })(),
//     },
//   },
// ];

// export const periodArrayEnglish = [
//   {
//     label: 'Today',
//     value: {
//       from: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Yesterday',
//     value: {
//       from: (() => {
//         const y = subDays(new Date(), 1);
//         const date = y.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const y = subDays(new Date(), 1);
//         const date = y.toLocaleDateString('en-CA');
//         const endDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(new Date(), { weekStartsOn: 1 });
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last 7 Days',
//     value: {
//       from: (() => {
//         const from = subDays(new Date(), 6);
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const to = endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         const date = to.toLocaleDateString('en-CA');
//         const endDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Month',
//     value: {
//       from: (() => {
//         const from = startOfMonth(new Date());
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last Month',
//     value: {
//       from: (() => {
//         const from = startOfMonth(subMonths(new Date(), 1));
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const to = endOfMonth(subMonths(new Date(), 1));
//         const date = to.toLocaleDateString('en-CA');
//         const endDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last 3 Months',
//     value: {
//       from: (() => {
//         const from = startOfMonth(subMonths(new Date(), 3));
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'All',
//     value: {
//       from: (() => {
//         const date = userData?.financialStartYear;
//         return {
//           dateTime: `${date}T00:00:00.000Z`,
//           date,
//         };
//       })(),
//       to: (() => {
//         const date = userData?.financialEndYear;
//         return {
//           dateTime: `${date}T23:59:59.999Z`,
//           date,
//         };
//       })(),
//     },
//   },
// ];

export const getAdFromBS = (value: string) => {
  if (!value) return null;

  const bsDate = new NepaliDate(value);
  const jsDate = bsDate.toJsDate();
  jsDate.setHours(12);
  return dayjs(jsDate);
};

export const getAdFromBSInYMD = (date: any) => {
  if (!date) return null;

  return `${date?.$y}-${String(date?.$M + 1).padStart(2, '0')}-${String(date?.$D).padStart(2, '0')}`;
};

// export const compareFinancialEndDate = (date: string) => {
//   const date1 = new Date(date);
//   const date2 = new Date(userData?.financialEndYear);
//   return date1 > date2 ? userData?.financialEndYear : date;
// };

// export const compareFinancialStartDate = (date: string) => {
//   const date1 = new Date(date);
//   const date2 = new Date(userData?.financialStartYear);
//   return date1 < date2 ? userData?.financialStartYear : date;
// };

// export const periodArrayEnglish = [
//   {
//     label: 'Today',
//     value: {
//       from: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Yesterday',
//     value: {
//       from: (() => {
//         const y = subDays(new Date(), 1);
//         const date = y.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const y = subDays(new Date(), 1);
//         const date = y.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(new Date(), { weekStartsOn: 1 });
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last 7 Days',
//     value: {
//       from: (() => {
//         const from = subDays(new Date(), 6);
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last Week',
//     value: {
//       from: (() => {
//         const from = startOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const to = endOfWeek(subDays(new Date(), 7), { weekStartsOn: 1 });
//         const date = to.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'This Month',
//     value: {
//       from: (() => {
//         const from = startOfMonth(new Date());
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last Month',
//     value: {
//       from: (() => {
//         const from = startOfMonth(subMonths(new Date(), 1));
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const to = endOfMonth(subMonths(new Date(), 1));
//         const date = to.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'Last 3 Months',
//     value: {
//       from: (() => {
//         const from = startOfMonth(subMonths(new Date(), 3));
//         const date = from.toLocaleDateString('en-CA');
//         const startDate = compareFinancialStartDate(date);
//         return {
//           dateTime: `${startDate}T00:00:00.000Z`,
//           date: startDate,
//         };
//       })(),
//       to: (() => {
//         const now = new Date();
//         const date = now.toLocaleDateString('en-CA');
//         const endDate = compareFinancialEndDate(date);
//         return {
//           dateTime: `${endDate}T23:59:59.999Z`,
//           date: endDate,
//         };
//       })(),
//     },
//   },
//   {
//     label: 'All',
//     value: {
//       from: (() => {
//         const date = userData?.financialStartYear;
//         return {
//           dateTime: `${date}T00:00:00.000Z`,
//           date,
//         };
//       })(),
//       to: (() => {
//         const date = userData?.financialEndYear;
//         return {
//           dateTime: `${date}T23:59:59.999Z`,
//           date,
//         };
//       })(),
//     },
//   },
// ];
